/* AdminBusinessCustomers - Beheer zakelijke klanten: aanvragen inzien, goedkeuren, afwijzen, blokkeren.
   Updated: Initial creation - admin page for managing business customer applications. */

import { useEffect, useState, useCallback } from 'react';
import Breadcrumb from '@/components/admin/Breadcrumb';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Building2, Check, X, Ban, RefreshCw, Search, Eye, Lock, Loader as Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

interface BusinessCustomer {
  id: string;
  email: string;
  company_name: string;
  contact_first_name: string;
  contact_last_name: string;
  kvk_number: string;
  btw_number: string | null;
  phone: string;
  country: string;
  postal_code: string;
  street: string;
  house_number: string;
  city: string;
  status: string;
  must_reset_password: boolean;
  auth_user_id: string | null;
  notes: string | null;
  created_at: string;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'In afwachting',
  approved: 'Goedgekeurd',
  rejected: 'Afgewezen',
  blocked: 'Geblokkeerd',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  blocked: 'bg-gray-200 text-gray-700 border-gray-300',
};

export default function AdminBusinessCustomers() {
  const [customers, setCustomers] = useState<BusinessCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState<BusinessCustomer | null>(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetPassword, setResetPassword] = useState('');

  const fetchCustomers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('business_customers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers((data || []) as BusinessCustomer[]);
    } catch {
      toast.error('Kon zakelijke klanten niet ophalen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const filtered = customers.filter(c => {
    const matchesSearch = !search ||
      c.company_name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      c.kvk_number.includes(search);
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleApprove = async () => {
    if (!selectedCustomer || !tempPassword || tempPassword.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 tekens lang zijn');
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('approve_business_customer', {
        p_customer_id: selectedCustomer.id,
        p_temp_password: tempPassword,
      });

      if (error) throw error;

      toast.success(`${selectedCustomer.company_name} is goedgekeurd`);
      setShowApproveDialog(false);
      setTempPassword('');
      setSelectedCustomer(null);
      fetchCustomers();
    } catch (err: any) {
      toast.error(err.message || 'Goedkeuren mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (customer: BusinessCustomer) => {
    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('reject_business_customer', {
        p_customer_id: customer.id,
      });
      if (error) throw error;
      toast.success(`${customer.company_name} is afgewezen`);
      fetchCustomers();
    } catch (err: any) {
      toast.error(err.message || 'Afwijzen mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlock = async (customer: BusinessCustomer) => {
    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('block_business_customer', {
        p_customer_id: customer.id,
      });
      if (error) throw error;
      toast.success(`${customer.company_name} is geblokkeerd`);
      fetchCustomers();
    } catch (err: any) {
      toast.error(err.message || 'Blokkeren mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (customer: BusinessCustomer) => {
    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('reactivate_business_customer', {
        p_customer_id: customer.id,
      });
      if (error) throw error;
      toast.success(`${customer.company_name} is opnieuw geactiveerd`);
      fetchCustomers();
    } catch (err: any) {
      toast.error(err.message || 'Reactiveren mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedCustomer || !resetPassword || resetPassword.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 tekens lang zijn');
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('reset_business_password', {
        p_customer_id: selectedCustomer.id,
        p_new_password: resetPassword,
      });
      if (error) throw error;
      toast.success('Wachtwoord gereset');
      setShowResetDialog(false);
      setResetPassword('');
      setSelectedCustomer(null);
      fetchCustomers();
    } catch (err: any) {
      toast.error(err.message || 'Wachtwoord resetten mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <Breadcrumb items={[{ label: 'Zakelijk', path: '/kh-admin/zakelijk/klanten' }, { label: 'Klanten' }]} />
      <h1 className="text-4xl font-bold text-[#1e2b22] mb-2">Zakelijke klanten</h1>
      <p className="text-[#5a6552] mb-8">Beheer aanvragen en accounts van zakelijke klanten</p>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5a6552]" />
          <Input
            type="text"
            placeholder="Zoek op bedrijfsnaam, e-mail of KVK..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-10 bg-white border-[#e7e1d6] text-[#1e2b22] focus:border-[#1e2b22]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-3 rounded-md border border-[#e7e1d6] bg-white text-[#1e2b22] text-sm focus:border-[#1e2b22]"
        >
          <option value="all">Alle statussen</option>
          <option value="pending">In afwachting</option>
          <option value="approved">Goedgekeurd</option>
          <option value="rejected">Afgewezen</option>
          <option value="blocked">Geblokkeerd</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#e7e1d6] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#5a6552]">Laden...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#5a6552]">Geen zakelijke klanten gevonden</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Bedrijf</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">KVK</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Aangemaakt</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e7e1d6]">
                {filtered.map((customer) => (
                  <tr key={customer.id} className="hover:bg-[#e7e1d6]/30 transition-colors">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-[#1e2b22]">{customer.company_name}</p>
                      <p className="text-xs text-[#5a6552]">{customer.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#1e2b22]">{customer.contact_first_name} {customer.contact_last_name}</p>
                      <p className="text-xs text-[#5a6552]">{customer.phone}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#5a6552]">{customer.kvk_number}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[customer.status]}`}>
                        {STATUS_LABELS[customer.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#5a6552]">{formatDate(customer.created_at)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedCustomer(customer)}
                          className="text-[#5a6552] hover:bg-[#e7e1d6]/50"
                          title="Details bekijken"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {customer.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedCustomer(customer); setShowApproveDialog(true); }}
                              className="text-green-600 hover:bg-green-50"
                              title="Goedkeuren"
                              disabled={actionLoading}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReject(customer)}
                              className="text-red-500 hover:bg-red-50"
                              title="Afwijzen"
                              disabled={actionLoading}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {customer.status === 'approved' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedCustomer(customer); setShowResetDialog(true); }}
                              className="text-[#5a6552] hover:bg-[#e7e1d6]/50"
                              title="Wachtwoord resetten"
                              disabled={actionLoading}
                            >
                              <Lock className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleBlock(customer)}
                              className="text-gray-500 hover:bg-gray-100"
                              title="Blokkeren"
                              disabled={actionLoading}
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {(customer.status === 'rejected' || customer.status === 'blocked') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReactivate(customer)}
                            className="text-green-600 hover:bg-green-50"
                            title="Opnieuw activeren"
                            disabled={actionLoading}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      {selectedCustomer && !showApproveDialog && !showResetDialog && (
        <Dialog open={!!selectedCustomer} onOpenChange={(open) => { if (!open) setSelectedCustomer(null); }}>
          <DialogContent className="max-w-lg border-[#e7e1d6] bg-[#fcfaf6]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1e2b22] flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#5a6552]" />
                {selectedCustomer.company_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-[#5a6552] uppercase tracking-wide">Contactpersoon</p>
                  <p className="text-[#1e2b22] font-medium">{selectedCustomer.contact_first_name} {selectedCustomer.contact_last_name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5a6552] uppercase tracking-wide">E-mail</p>
                  <p className="text-[#1e2b22] font-medium">{selectedCustomer.email}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5a6552] uppercase tracking-wide">Telefoon</p>
                  <p className="text-[#1e2b22] font-medium">{selectedCustomer.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5a6552] uppercase tracking-wide">KVK</p>
                  <p className="text-[#1e2b22] font-medium">{selectedCustomer.kvk_number}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5a6552] uppercase tracking-wide">BTW</p>
                  <p className="text-[#1e2b22] font-medium">{selectedCustomer.btw_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5a6552] uppercase tracking-wide">Status</p>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[selectedCustomer.status]}`}>
                    {STATUS_LABELS[selectedCustomer.status]}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs text-[#5a6552] uppercase tracking-wide mb-1">Adres</p>
                <p className="text-[#1e2b22] font-medium">
                  {selectedCustomer.street} {selectedCustomer.house_number}<br />
                  {selectedCustomer.postal_code} {selectedCustomer.city}, {selectedCustomer.country}
                </p>
              </div>
              {selectedCustomer.notes && (
                <div>
                  <p className="text-xs text-[#5a6552] uppercase tracking-wide mb-1">Notities</p>
                  <p className="text-[#1e2b22]">{selectedCustomer.notes}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedCustomer(null)} className="border-[#e7e1d6] text-[#5a6552]">
                Sluiten
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Approve dialog */}
      {showApproveDialog && selectedCustomer && (
        <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <DialogContent className="max-w-md border-[#e7e1d6] bg-[#fcfaf6]">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-[#1e2b22]">Klant goedkeuren</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#5a6552] mb-4">
              Je keurt <span className="font-medium text-[#1e2b22]">{selectedCustomer.company_name}</span> goed. Stel een tijdelijk wachtwoord in dat de klant bij de eerste login moet wijzigen.
            </p>
            <div className="space-y-2">
              <Label htmlFor="temp-password" className="text-sm font-medium text-[#1e2b22]">
                Tijdelijk wachtwoord (min. 8 tekens)
              </Label>
              <Input
                id="temp-password"
                type="text"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                placeholder="Bijv. Buitn2026!"
                className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] focus:border-[#1e2b22]"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setShowApproveDialog(false); setTempPassword(''); }}
                className="border-[#e7e1d6] text-[#5a6552]"
              >
                Annuleren
              </Button>
              <Button
                onClick={handleApprove}
                disabled={actionLoading || !tempPassword || tempPassword.length < 8}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                Goedkeuren
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Reset password dialog */}
      {showResetDialog && selectedCustomer && (
        <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <DialogContent className="max-w-md border-[#e7e1d6] bg-[#fcfaf6]">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-[#1e2b22]">Wachtwoord resetten</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#5a6552] mb-4">
              Stel een nieuw tijdelijk wachtwoord in voor <span className="font-medium text-[#1e2b22]">{selectedCustomer.company_name}</span>. De klant moet dit bij de volgende login wijzigen.
            </p>
            <div className="space-y-2">
              <Label htmlFor="reset-pw" className="text-sm font-medium text-[#1e2b22]">
                Nieuw wachtwoord (min. 8 tekens)
              </Label>
              <Input
                id="reset-pw"
                type="text"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Bijv. Buitn2026!"
                className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] focus:border-[#1e2b22]"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setShowResetDialog(false); setResetPassword(''); }}
                className="border-[#e7e1d6] text-[#5a6552]"
              >
                Annuleren
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={actionLoading || !resetPassword || resetPassword.length < 8}
                className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                Resetten
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
